/**
 * Flight Thank You Thunk
 *
 * Fetches flight booking confirmation details with polling support
 * GET /flights/thank-you
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { FlightThankYouResponse } from '@contracts';
import {
  fetchFlightThankYou,
  isBookingProcessing,
  FlightThankYouError,
} from '../../api/flightBookingApi';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';

// ============================================
// Types
// ============================================

interface FetchFlightThankYouParams {
  /** Conversation/Chat Request ID */
  crId: string;
  /** Itinerary ID */
  itId: string;
  /** Currency code (default: INR) */
  currency?: string;
  /** Language code (default: eng) */
  language?: string;
  /** Region code (default: in) */
  region?: string;
}

interface FetchFlightThankYouResult {
  data: FlightThankYouResponse;
  /** Indicates if polling should continue */
  shouldPoll: boolean;
}

// ============================================
// Constants
// ============================================

/** Polling interval in milliseconds (2 seconds) */
const POLLING_INTERVAL_MS = 2000;

/** Maximum number of polling attempts */
const MAX_POLL_ATTEMPTS = 30; // 30 attempts * 2s = 1 minute max

/** Current poll attempt counter */
let pollAttemptCount = 0;

// ============================================
// Thunk
// ============================================

/**
 * Fetch flight thank you (booking confirmation) details with polling
 * GET /flights/thank-you
 *
 * Polls the API until booking reaches a final state or max attempts reached
 */
export const fetchFlightThankYouThunk = createAsyncThunk<
  FetchFlightThankYouResult,
  FetchFlightThankYouParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'flightThankYou/fetch',
  async (params, { rejectWithValue }) => {
    try {
      const { crId, itId, currency = 'INR', language = 'eng', region = 'in' } = params;
      const sessionId = getOrCreateSessionId();

      const response = await fetchFlightThankYou({
        crId,
        itId,
        sessionId,
        currency,
        language,
        region,
      });

      // Handle API error response
      if (!response.success || !response.data) {
        const errorMessage =
          response.error?.message ?? 'Failed to fetch flight booking confirmation';
        logger.error('[FlightThankYouThunk] API returned error:', response.error);
        return rejectWithValue(errorMessage);
      }

      // Check booking status
      const bookingStatus = response.data.bookingStatus.type;
      const shouldPoll = isBookingProcessing(bookingStatus);

      logger.warn('[FlightThankYouThunk] Booking status:', {
        status: bookingStatus,
        shouldPoll,
        pollAttempt: pollAttemptCount,
      });

      return {
        data: response.data,
        shouldPoll,
      };
    } catch (error) {
      logger.error('[FlightThankYouThunk] Failed to fetch flight booking confirmation:', error);
      if (error instanceof FlightThankYouError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch flight booking confirmation');
    }
  },
  {
    // Prevent duplicate API calls - condition runs BEFORE pending action is dispatched
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isLoading } = state.flightThankYou;
      // Return false to cancel the thunk if already loading
      return !isLoading;
    },
  },
);

/**
 * Start polling for flight booking confirmation
 * Dispatches fetchFlightThankYouThunk at regular intervals until final state
 */
export const pollFlightThankYouThunk = createAsyncThunk<
  void,
  FetchFlightThankYouParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('flightThankYou/poll', async (params, { dispatch, rejectWithValue }) => {
  // Reset poll counter
  pollAttemptCount = 0;

  /**
   * Poll implementation
   */
  const poll = async (): Promise<void> => {
    try {
      pollAttemptCount += 1;

      logger.warn('[FlightThankYouThunk] Polling attempt:', pollAttemptCount);

      // Check max attempts
      if (pollAttemptCount > MAX_POLL_ATTEMPTS) {
        logger.error('[FlightThankYouThunk] Max poll attempts reached');
        return Promise.reject('Maximum polling attempts reached');
      }

      // Fetch booking status
      const result = await dispatch(fetchFlightThankYouThunk(params)).unwrap();

      // If booking is still processing, schedule next poll
      if (result.shouldPoll) {
        // Schedule next poll
        await new Promise((resolve) => {
          setTimeout(resolve, POLLING_INTERVAL_MS);
        });
        return poll(); // Recursive call
      }

      // Booking reached final state
      logger.warn('[FlightThankYouThunk] Booking reached final state');
      return Promise.resolve();
    } catch (error) {
      logger.error('[FlightThankYouThunk] Polling error:', error);
      return Promise.reject(error);
    }
  };

  try {
    await poll();
    return Promise.resolve();
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to poll booking confirmation';
    return rejectWithValue(errorMessage);
  }
});

/**
 * Reset poll attempt counter
 */
export function resetPollCount(): void {
  pollAttemptCount = 0;
}
